import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  Skeleton,
  Collapse,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { connect } from "react-redux";
import {
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  centered_flex_box,
  left_flex_box,
  main_button,
  MainInput,
} from "../app/components/Styles";
import { isMobile } from "react-device-detect";
import { Image, Space, Table, Tag } from "antd";
import Logo from "../app/components/logo.png";
import PropTypes from "prop-types";
import MuiPhoneNumber from "material-ui-phone-number";
import ImageComponent from "../app/components/ImageComponent";
import { exportOrders, getOrders } from "../app/store/actions/dataActions";
import { useEffect } from "react";
import { Search } from "@mui/icons-material";
import moment from "moment";
import DateRangePicker from "../app/components/DateRangePicker";
import { useTranslation } from 'react-i18next';
import CreateTicketModal from "../app/components/CreateTicketModal";
import { OrderLineItemsExpandedTable } from "../app/components/OrderLineItemsExpandedTable";
import ReturnInboundSection from "../app/components/ReturnInboundSection";
import { cardShell, StatusPill, formatInt } from '../app/glass/styles';
import { buildOrderHubColumns, mapFulfillmentListRow } from '../app/glass/OrderTableCells';
import SearchFilterBar, { FilterChips, exportIcon, plusIcon } from '../app/glass/SearchFilterBar';
import { patchSearchParams } from '../app/utils/searchParams';

/** Chips that map 1:1 to API `status` query values. */
const STATUS_CHIPS = [
  ['', 'orders.allStatuses'],
  ['Ready for Pickup', 'orders.statusReadyForPickup'],
  ['Out for Delivery', 'orders.statusOutForDelivery'],
  ['Returned', 'returns.statusReturned'],
  ['Returned to Chainz', 'returns.statusReturnedToChainz'],
];

export const Returns = ({
  getOrders,
  exportOrders,
  user,
  growthPercentage,
  returnAnalysis,
  orders,
  isLoading,
  isWaiting,
  token
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setListParams = React.useCallback((updates) => {
    setSearchParams(patchSearchParams(searchParams, { tab: 'returns', ...updates }), { replace: true });
  }, [searchParams, setSearchParams]);
  const [error, setError] = React.useState(null);
  const [terms, setTerms] = React.useState(false);
  const [page, setPage] = React.useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize')) || 10);
  const [startDate, setStartDate] = React.useState(searchParams.get('startDate') ? moment(searchParams.get('startDate')) : null)
  const [endDate, setEndDate] = React.useState(searchParams.get('endDate') ? moment(searchParams.get('endDate')) : null)
  const [search, setSearch] = React.useState(searchParams.get('search') || '');
  const [status, setStatus] = React.useState(searchParams.get('status') || '');
  const [filters, setFilters] = React.useState(searchParams.get('filters') ? JSON.parse(searchParams.get('filters')) : {});
  const [showFilters, setShowFilters] = React.useState(false);
  const [helpTicketOpen, setHelpTicketOpen] = React.useState(false);
  const [helpTicketContext, setHelpTicketContext] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  // Add handlers for filter changes
  const handleSearchChange = (value) => {
    setPage(1);
    setSearch(value);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search: value,
      status,
      filters: JSON.stringify(filters)
    });
  };

  const handleStatusChange = (value) => {
    setPage(1);
    setStatus(value);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search,
      status: value,
      filters: JSON.stringify(filters)
    });
  };

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setPage(1);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Restore state when returning from order view
  useEffect(() => {
    if (location.state?.from === '/returns' && location.state?.state) {
      const { page, pageSize, startDate, endDate, search, status, filters } = location.state.state;
      setPage(page);
      setPageSize(pageSize);
      setStartDate(moment(startDate));
      setEndDate(moment(endDate));
      setSearch(search || '');
      setStatus(status || '');
      setFilters(JSON.parse(filters));
      setListParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        startDate,
        endDate,
        search,
        status,
        filters
      });
    }
  }, [location.state]);

  useEffect(() => {
    if ((startDate && !endDate) || (!startDate && endDate)) {
      return; // Don't proceed if only one date is set
    }
    const params = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      page,
      limit: pageSize,
      isReturn: true,
      status: status || undefined,
      ...filters
    };
    getOrders(params);
  }, [page, pageSize, startDate, endDate, status, filters])

  const handleSearch = () => {
    setPage(1);
    const params = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      page: 1,
      limit: pageSize,
      isReturn: true,
      search: search || undefined,
      status: status || undefined,
      ...filters
    };
    getOrders(params);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search: search || undefined,
      status: status || undefined,
      filters: JSON.stringify(filters)
    });
  };

  const clearAllFilters = () => {
    setSearch('');
    setStatus('');
    setStartDate(null);
    setEndDate(null);
    setFilters({});
    setPage(1);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: null,
      endDate: null,
      search: null,
      status: null,
      filters: null,
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
    setFilters(filters);
    setListParams({
      page: pagination.current.toString(),
      pageSize: pagination.pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search: search || undefined,
      status: status || undefined,
      filters: JSON.stringify(filters)
    });
  }

  const listState = {
    page,
    pageSize,
    startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
    endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
    search,
    status,
    filters: JSON.stringify(filters),
  };

  const renderActions = (_, { _id, returnObj }) => (
    <Space wrap direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
      <Button
        size="small"
        sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem", width: isMobile ? '100%' : 'auto' }}
        onClick={() => {
          navigate(`/orders/${_id}`, { state: { from: '/returns', state: listState } });
        }}
      >
        {t('orders.viewOrder')}
      </Button>
      <Button
        size="small"
        sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem", width: isMobile ? '100%' : 'auto' }}
        onClick={() => {
          navigate(`/orders/${returnObj.originalOrder}`, { state: { from: '/returns', state: listState } });
        }}
      >
        {t('returns.viewOriginalOrder') || 'View Original Order'}
      </Button>
      <Button
        size="small"
        sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem", width: isMobile ? '100%' : 'auto' }}
        onClick={() => { setHelpTicketContext({ ticketType: 'Order', relevantOrder: _id }); setHelpTicketOpen(true); }}
      >
        {t('helpDesk.getHelp')}
      </Button>
    </Space>
  );

  const columns = buildOrderHubColumns({
    t,
    isMobile,
    renderStatus: (_, { status: orderStatus }) => <StatusPill status={orderStatus}>{orderStatus || '—'}</StatusPill>,
    renderActions,
  });

  const expandedRowRender = (record) => (
    <Box>
      <OrderLineItemsExpandedTable items={record.items} showHeader={!isMobile} />
      <ReturnInboundSection order={record} />
    </Box>
  );

  const data = orders?.docs?.map((row) => ({
    ...mapFulfillmentListRow(row, {
      t,
      vendor: user?.vendor,
      typeLabel: t('orders.typeReturn'),
      status: row.fulfillment ? row.status : 'INVALID',
    }),
    isReturn: true,
  }));

  const handleDownloadExcel = () => {
    const params = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search: search || undefined,
      status: status || undefined,
      isReturn: true,
    };
    exportOrders(params);
  }

  const pctOfTotal = (n) =>
    n && orders?.total ? (((n / orders.total) * 100) || 0).toFixed(1) : '0.0';

  const kpiItems = [
    {
      label: t('home.totalReturns'),
      value: formatInt(orders?.totalDocs),
      sub: `${growthPercentage > 0 ? `+${growthPercentage}` : growthPercentage ?? 0}% ${t('orders.sinceLastMonth')}`,
    },
    {
      label: t('home.defectiveItems'),
      value: formatInt(returnAnalysis?.totalDefective),
      sub: `${pctOfTotal(returnAnalysis?.totalDefective)}% of total`,
    },
    {
      label: t('home.customerIssues'),
      value: formatInt(returnAnalysis?.totalCustomerErrors),
      sub: `${pctOfTotal(returnAnalysis?.totalCustomerErrors)}% of total`,
    },
    {
      label: t('home.wrongItems'),
      value: formatInt(returnAnalysis?.totalWarehouseMistake),
      sub: `${pctOfTotal(returnAnalysis?.totalWarehouseMistake)}% of total`,
    },
  ];

  return (
    <div>
      <Box sx={{ width: "100%", display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div
          className="cz-g2"
          style={{
            background: 'var(--card-solid)',
            borderRadius: 26,
            padding: isMobile ? '18px 20px' : '24px 34px',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 24,
            alignItems: 'center',
            border: '1px solid var(--s12)',
            boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
          }}
        >
          {kpiItems.map((st) => (
            <div key={st.label}>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{st.label}</div>
              <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 500 }}>{isLoading ? <Skeleton width={60} /> : st.value}</div>
              <div style={{ fontSize: 11, color: 'var(--mut2)', marginTop: 6 }}>{st.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, color: 'var(--mut)' }}>
          {t('orders.statisticsDateRangeNote', {
            from: startDate ? startDate.format("YYYY-MM-DD") : moment().startOf("month").format("YYYY-MM-DD"),
            to: endDate ? endDate.format("YYYY-MM-DD") : moment().endOf("month").format("YYYY-MM-DD")
          })}
        </div>

        <FilterChips
          value={status}
          onChange={handleStatusChange}
          options={STATUS_CHIPS.map(([value, labelKey]) => [value, t(labelKey, { defaultValue: value || 'All' })])}
        />

        <SearchFilterBar
          placeholder={t('orders.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          onSearch={handleSearch}
          isMobile={isMobile}
          filterOpen={showFilters}
          onToggleFilter={() => setShowFilters(!showFilters)}
          filterLabel={t('common.filter')}
          actions={(
            <>
              <button type="button" className="cz-chip" onClick={handleDownloadExcel} disabled={isWaiting}>
                {t('common.export')}
                {exportIcon}
              </button>
              <button type="button" className="cz-chip red" onClick={() => navigate("/external-return")}>
                {t('returns.addExternalReturn') || 'Add External Return'}
                {plusIcon}
              </button>
            </>
          )}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ color: 'var(--tx2)' }}>{t('dateRange.selectDateRange')}</Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sx={{ ...centered_flex_box }}>
                <Button sx={{ ...main_button }} fullWidth onClick={() => clearAllFilters()}>{t('common.clear')}</Button>
              </Grid>
            </Grid>
          </Box>
        </SearchFilterBar>
        <Box sx={{
          width: "100%",
          overflowX: "auto",
          "& .ant-table-wrapper": { width: "100%" },
          "& .ant-table": { minWidth: isMobile ? 300 : 1480 },
          "& .ant-table-cell": { whiteSpace: isMobile ? "normal" : "nowrap" }
        }}>
          <Table
            columns={columns}
            dataSource={data}
            loading={isLoading}
            rowKey="_id"
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => (record.items?.length > 0),
            }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: orders?.totalDocs,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total) => `${t('table.total')} ${total} ${t('table.items')}`,
            }}
            onChange={handleTableChange}
            showSorterTooltip={{
              target: 'sorter-icon',
            }}
            scroll={{ x: isMobile ? 300 : 1480 }}
          />
        </Box>
      </Box>
      <CreateTicketModal
        open={helpTicketOpen}
        onClose={() => { setHelpTicketOpen(false); setHelpTicketContext(null); }}
        token={token}
        context={helpTicketContext}
        onSuccess={() => setHelpTicketOpen(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  isWaiting: state?.wait?.isLoading,
  token: state?.auth?.token,
  orders: state?.records?.orders,
  returnAnalysis: state?.records?.returnAnalysis,
  growthPercentage: state?.records?.growthPercentage
});

const mapDispatchToProps = { getOrders, exportOrders };

export default connect(mapStateToProps, mapDispatchToProps)(Returns);
